<script lang="ts" setup>
import { Sort } from "@element-plus/icons-vue";
import { ElNotification } from "element-plus";
import { useCustomerProfileStore } from "~/stores/customerProfileStore";
import { useDebouncedSearch } from "~/composable/useDebouncedSearch";
import { useAuthStore } from "~/stores/auth";
import { PermissionNames } from "~/configs/permission_names";
import { MergeCompareRow, MergePreviewSide } from "~/types/profile";

const profileStore = useCustomerProfileStore();
const authStore = useAuthStore();

// The right-hand profile is the one that gets deleted, so a user who cannot see
// other people's profiles may only pick one they are the ticket agent for. The
// left-hand ("kept") select keeps the full list.
//
// Same condition components/profile/Filters.vue uses to pin the profiles list's
// own Ticket Agent filter, so the two agree on who is restricted.
const childAgentLocked = computed(
  () => !isUserPermitted(PermissionNames.PROFILE_SHOW_OTHERS),
);

// Falls back to -1, an id no user has, rather than to null if the auth user is
// somehow not loaded. The endpoint applies the scope through `when($ticketAgentId)`,
// which reads null - and 0 - as "no filter", so a null here would hand back the
// unscoped list, which is the one outcome this must never produce.
const ownTicketAgentId = computed(() => authStore.user?.id ?? -1);

const childScope = () =>
  childAgentLocked.value ? { ticket_agent_id: ownTicketAgentId.value } : {};

const parentId = ref<number | null>(null);
const childId = ref<number | null>(null);
const merging = ref(false);
const swapping = ref(false);
const showFullCompare = ref(false);
const openSections = ref(["related", "fields", "custom_fields"]);

const defaultAvatar = "/images/avatar.png";

const preview = computed(() => profileStore.mergePreview);
const bothSelected = computed(
  () => !!parentId.value && !!childId.value && parentId.value !== childId.value,
);

const previewMatchesSelection = computed(
  () =>
    !!preview.value?.child &&
    preview.value.parent.id === parentId.value &&
    preview.value.child.id === childId.value,
);

const loadPreview = async () => {
  if (!parentId.value) {
    profileStore.clearMergePreview();
    return;
  }

  const { error } = await profileStore.getMergePreview(
    parentId.value,
    bothSelected.value ? childId.value : null,
  );

  if (error) {
    ElNotification({
      type: "error",
      title: "Could not load profile details",
      message: "Please try selecting the profiles again",
    });
  }
};

const handleClose = () => {
  profileStore.toggleProfileMergeModal(false);
  parentId.value = null;
  childId.value = null;
  merging.value = false;
  swapping.value = false;
  showFullCompare.value = false;
  childProfiles.value = [];
  profileStore.clearMergePreview();
  profileStore._selectedProfileForEdit = {};
  profileStore._mergeConflictPhone = "";
  profileStore._mergeRedirectMode = "profile";
};

const { remoteMethod, loading } = useDebouncedSearch((query) => {
  profileStore._simpleFilter = { search: query };
  profileStore.getSimpleProfiles();
});

interface SelectOption {
  id: number;
  name: string | null;
  profile_picture?: { public_url: string } | null;
}

// Held here instead of in the store: the shared `simpleProfiles` array feeds a
// dozen other selects, so narrowing it in place would quietly shrink all of
// them. Only this select's list is scoped.
const childProfiles = ref<SelectOption[]>([]);

const loadChildProfiles = async (search = "") => {
  const profiles = await profileStore.fetchSimpleProfiles({
    ...(search ? { search } : {}),
    ...childScope(),
  });

  if (profiles) {
    childProfiles.value = profiles as SelectOption[];
  }
};

const { remoteMethod: childRemoteMethod, loading: childLoading } =
  useDebouncedSearch((query) => {
    loadChildProfiles(query);
  });

const profileOptionLabel = (profile: SelectOption) =>
  `${profile.id} - ${profile.name ?? ""}`;

const withSelected = (
  selectedId: number | null,
  side: MergePreviewSide | null,
  options: SelectOption[],
): SelectOption[] => {
  if (!selectedId || options.some((option) => option.id === selectedId)) {
    return options;
  }

  if (side?.id !== selectedId) return options;

  return [
    {
      id: side.id,
      name: side.name,
      profile_picture: side.profile_picture_url
        ? { public_url: side.profile_picture_url }
        : null,
    },
    ...options,
  ];
};

const parentOptions = computed(() =>
  withSelected(
    parentId.value,
    preview.value?.parent ?? null,
    profileStore.simpleProfiles as SelectOption[],
  ),
);

const childOptions = computed(() =>
  withSelected(
    childId.value,
    preview.value?.child ?? null,
    childProfiles.value,
  ),
);

const sideHeading = (side: MergePreviewSide | null, suffix: string) =>
  side ? `${side.id} - ${side.name ?? ""} · ${suffix}` : suffix;

const onSelectionChange = async () => {
  showFullCompare.value = false;
  await loadPreview();
};

const swapProfiles = async () => {
  if (swapping.value) return;

  // Read once: the guard below awaits, and a second click landing in that window
  // would otherwise check the id that the first swap had already moved.
  const incomingChildId = parentId.value;

  // Swapping moves the left profile into the delete slot, which is a way around
  // the scope on the right select. Check the profile really is one this user is
  // the ticket agent for before letting it across.
  if (childAgentLocked.value && incomingChildId) {
    swapping.value = true;
    const allowed = await profileStore.fetchSimpleProfiles({
      id: incomingChildId,
      ...childScope(),
    });
    swapping.value = false;

    // null means the check itself failed. Saying "you may not" then would be a
    // lie about permission when the truth is that nothing was checked.
    if (allowed === null) {
      ElNotification({
        type: "error",
        title: "Could not check that profile",
        message: "Please try swapping again",
      });
      return;
    }

    if (!allowed.some((profile) => profile.id === incomingChildId)) {
      ElNotification({
        type: "warning",
        title: "Cannot swap",
        message: "You can only delete profiles you are the ticket agent for.",
      });
      return;
    }
  }

  // The profile moving into the right slot has to exist in that select's own
  // (scoped) list or el-select falls back to rendering a bare id. Its details
  // are already on the loaded preview, so carry them across before swapping —
  // with no right-hand profile the preview is cleared and cannot supply them
  // afterwards.
  const incomingChild = preview.value?.parent ?? null;

  [parentId.value, childId.value] = [childId.value, parentId.value];

  if (
    incomingChild &&
    incomingChild.id === childId.value &&
    !childProfiles.value.some((profile) => profile.id === incomingChild.id)
  ) {
    childProfiles.value = [
      {
        id: incomingChild.id,
        name: incomingChild.name,
        profile_picture: incomingChild.profile_picture_url
          ? { public_url: incomingChild.profile_picture_url }
          : null,
      },
      ...childProfiles.value,
    ];
  }

  await loadPreview();
};

const setChildFromConflictPhone = async () => {
  if (!profileStore._mergeConflictPhone) return;

  // Scoped like the select itself, so a duplicate phone that turns out to sit on
  // another agent's profile is simply not preselected rather than handed over
  // for deletion.
  const profiles = await profileStore.fetchSimpleProfiles({
    search: profileStore._mergeConflictPhone,
    ...childScope(),
  });

  const matched = profiles?.find((profile) => profile.id !== parentId.value);

  if (!matched) return;

  childId.value = matched.id;

  if (!childProfiles.value.some((profile) => profile.id === matched.id)) {
    childProfiles.value = [matched as SelectOption, ...childProfiles.value];
  }

  await loadPreview();
};

watch(
  () => profileStore.showProfileMergeModal,
  async (isOpen) => {
    if (!isOpen) return;

    parentId.value = profileStore.selectedProfileForEdit?.id ?? null;
    childId.value = null;
    showFullCompare.value = false;

    profileStore.setSimpleFilter(parentId.value ? { id: parentId.value } : {});
    await profileStore.getSimpleProfiles();
    await loadChildProfiles();
    await loadPreview();
    await setChildFromConflictPhone();
  },
  { immediate: true },
);

const relatedRows = computed<MergeCompareRow[]>(
  () =>
    preview.value?.related.map((row) => ({
      key: row.key,
      label: row.moves ? row.label : `${row.label} (not carried over)`,
      parent: String(row.parent_count),
      child: String(row.child_count),
      result: String(row.result_count),
      status: row.moves ? undefined : row.child_count ? "dropped" : undefined,
    })) ?? [],
);

const fieldRows = computed<MergeCompareRow[]>(
  () => preview.value?.fields.map((row) => ({ ...row })) ?? [],
);

const customFieldRows = computed<MergeCompareRow[]>(
  () => preview.value?.custom_fields.map((row) => ({ ...row })) ?? [],
);

const orphanedCount = computed(
  () =>
    preview.value?.related
      .filter((row) => !row.moves)
      .reduce((total, row) => total + row.child_count, 0) ?? 0,
);

const mergeProfiles = async () => {
  merging.value = true;
  const { response, error } = await profileStore.mergeProfiles(
    [parentId.value, childId.value],
    true,
  );
  merging.value = false;

  if (response) {
    const keptId = parentId.value;
    profileStore.toggleProfileMergeModal(false);
    profileStore.clearMergePreview();
    ElNotification({ title: "Merged Successfully", type: "success" });

    if (profileStore._mergeRedirectMode === "profile") {
      await navigateTo(`/profiles/${keptId}`);
    }
    return;
  }

  if (error) {
    ElNotification({
      type: "error",
      title: "Merge Failed",
      message: "Failed to merge selected profiles",
    });
  }
};
</script>

<template>
  <el-dialog
    v-model="profileStore.showProfileMergeModal"
    :close-on-click-modal="false"
    :lock-scroll="false"
    top="4vh"
    :modal="false"
    :show-close="true"
    :destroy-on-close="true"
    draggable
    width="65%"
    @close="handleClose"
  >
    <template #header="{ titleId, titleClass }">
      <div class="my-header flex flex-col gap-1">
        <h4 :id="titleId" class="text-md" :class="titleClass">Merge Profile</h4>
        <span class="dialog-title-subtext">
          The right profile is merged into the left one, then deleted.
        </span>
      </div>
    </template>

    <div
      v-loading="profileStore.mergePreviewLoading"
      class="flex flex-col gap-6 pt-4"
    >
      <div class="grid grid-cols-[1fr_auto_1fr] items-end gap-4">
        <div class="form-elem-container">
          <label
            class="text-sm font-medium text-emerald-600 dark:text-emerald-400"
          >
            Left profile — kept
          </label>
          <el-select
            v-model="parentId"
            filterable
            clearable
            class="w-full"
            popper-class="profile-merge-select-dropdown"
            placeholder="Search by id, name, phone, email or address"
            remote
            reserve-keyword
            :remote-method="remoteMethod"
            :loading="loading"
            @change="onSelectionChange"
          >
            <el-option
              v-for="profile in parentOptions"
              :key="profile.id"
              :label="profileOptionLabel(profile)"
              :value="profile.id"
            >
              <div class="flex items-center gap-3">
                <img
                  class="h-10 w-10 shrink-0 rounded-full object-cover"
                  :src="profile.profile_picture?.public_url || defaultAvatar"
                  alt="profile picture"
                />
                <span class="truncate">{{ profileOptionLabel(profile) }}</span>
              </div>
            </el-option>
          </el-select>
        </div>

        <el-tooltip content="Swap which profile is kept" placement="top">
          <el-button
            :icon="Sort"
            circle
            class="mb-1"
            :loading="swapping"
            @click="swapProfiles"
          />
        </el-tooltip>

        <div class="form-elem-container">
          <label class="text-sm font-medium text-rose-600 dark:text-rose-400">
            Right profile — deleted
            <span
              v-if="childAgentLocked"
              class="ml-1 font-normal text-xs text-gray-400"
            >
              (only profiles you are the ticket agent for)
            </span>
          </label>
          <el-select
            v-model="childId"
            filterable
            clearable
            class="w-full"
            popper-class="profile-merge-select-dropdown"
            placeholder="Search by id, name, phone, email or address"
            remote
            reserve-keyword
            :remote-method="childRemoteMethod"
            :loading="childLoading"
            @change="onSelectionChange"
          >
            <el-option
              v-for="profile in childOptions"
              :key="profile.id"
              :label="profileOptionLabel(profile)"
              :value="profile.id"
            >
              <div class="flex items-center gap-3">
                <img
                  class="h-10 w-10 shrink-0 rounded-full object-cover"
                  :src="profile.profile_picture?.public_url || defaultAvatar"
                  alt="profile picture"
                />
                <span class="truncate">{{ profileOptionLabel(profile) }}</span>
              </div>
            </el-option>
          </el-select>
        </div>
      </div>

      <div v-if="preview" class="grid grid-cols-3 gap-4">
        <ProfileMergeSummaryCard
          :side="preview.parent"
          badge="Kept"
          badge-type="success"
        />

        <ProfileMergeSummaryCard
          v-if="preview.child"
          :side="preview.child"
          badge="Deleted"
          badge-type="danger"
        />
        <div
          v-else
          class="flex items-center justify-center rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-400 dark:border-gray-600"
        >
          Pick a profile to merge in
        </div>

        <ProfileMergeSummaryCard
          v-if="preview.result"
          :side="preview.result"
          badge="After merge"
          badge-type="primary"
        />
        <div
          v-else
          class="flex items-center justify-center rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-400 dark:border-gray-600"
        >
          The merged result appears here
        </div>
      </div>

      <el-empty
        v-else
        :image-size="70"
        description="Pick a profile to see its details."
      />

      <div v-if="previewMatchesSelection" class="flex justify-center">
        <el-button @click="showFullCompare = !showFullCompare">
          {{ showFullCompare ? "Hide full compare" : "Full compare" }}
        </el-button>
      </div>

      <el-collapse
        v-if="previewMatchesSelection && showFullCompare"
        v-model="openSections"
      >
        <el-collapse-item name="related">
          <template #title>
            <span class="font-medium">Related records</span>
            <span class="ml-2 text-xs text-gray-500">
              all of these move to the left profile — nothing is deleted
            </span>
          </template>

          <el-alert
            v-if="orphanedCount"
            type="warning"
            :closable="false"
            show-icon
            class="mb-3"
            title="Some related records are not carried over"
            description="Rows marked “not carried over” stay attached to the deleted profile."
          />

          <ProfileMergeCompareSection
            :rows="relatedRows"
            :parent-heading="sideHeading(preview?.parent ?? null, 'kept')"
            :child-heading="sideHeading(preview?.child ?? null, 'merged in')"
            child-tone="merged"
            result-heading="After merge"
            empty-text="Neither profile has related records."
          />
        </el-collapse-item>

        <el-collapse-item name="fields">
          <template #title>
            <span class="font-medium">Main columns</span>
          </template>

          <ProfileMergeCompareSection
            :rows="fieldRows"
            :parent-heading="sideHeading(preview?.parent ?? null, 'kept')"
            :child-heading="sideHeading(preview?.child ?? null, 'deleted')"
            result-heading="After merge"
            empty-text="Both profiles have the same column values."
          />
        </el-collapse-item>

        <el-collapse-item name="custom_fields">
          <template #title>
            <span class="font-medium">Custom columns</span>
          </template>

          <ProfileMergeCompareSection
            :rows="customFieldRows"
            :parent-heading="sideHeading(preview?.parent ?? null, 'kept')"
            :child-heading="sideHeading(preview?.child ?? null, 'deleted')"
            result-heading="After merge"
            empty-text="Neither profile has custom column values."
          />
        </el-collapse-item>
      </el-collapse>

      <div class="flex items-center justify-end gap-4 pt-2">
        <NavButton @click="handleClose">Cancel</NavButton>
        <el-button
          type="primary"
          :disabled="!previewMatchesSelection"
          :loading="merging"
          @click="mergeProfiles"
        >
          Merge
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<style scoped></style>

<style>
.profile-merge-select-dropdown .el-select-dropdown__item {
  height: auto;
  line-height: 1.4;
  padding-top: 6px;
  padding-bottom: 6px;
}
</style>
